import * as Sentry from '@sentry/tanstackstart-react';
import type { Query, QueryClient } from '@tanstack/react-query';

import { INVALIDATION_TABLE } from '#features/realtime/cacheInvalidation';
import { reconnectRefetchByCatchUp } from './reconnectRefetch';

/**
 * Догнать то, что прошло мимо, пока связи не было: перечитать на экране живые данные и то, что
 * устарело. Один проход на одно возвращение, откуда бы о нём ни узнали.
 *
 * Узнаём с нескольких сторон, и почти всегда сразу с двух: react-query видит возвращение сети
 * (`onlineManager`), веб ловит событие `online` после старта без сети, приложение - повторно
 * открытый сокет. Раньше каждая сторона перечитывала сама, и возврат сети стоил двух-трёх полных
 * проходов за три секунды. Теперь все зовут `catchUp`, а он пропускает вызовы, пришедшие в течение
 * `CATCH_UP_DEDUPE_MS` после предыдущего.
 *
 * Собственный перезапрос react-query по возвращении сети выключен по умолчанию
 * (`refetchOnReconnect: reconnectRefetchByCatchUp` в `queryClient`), иначе он был бы ещё одним
 * проходом. Запросы на паузе он продолжает по-прежнему сам.
 */
export type CatchUpReason = 'online' | 'socket';

export const CATCH_UP_DEDUPE_MS = 5_000;

const lastRunAt = new WeakMap<QueryClient, number>();

const followsCatchUp = (query: Query): boolean =>
  query.observers.some((observer) => {
    const option = observer.options.refetchOnReconnect;
    if (option === reconnectRefetchByCatchUp) {
      return true;
    }
    return (typeof option === 'function' ? option(query) : option) !== false;
  });

/**
 * Живые данные: их меняют события, которые без связи прошли мимо. Это всё, что роняет карта
 * инвалидации сокета (переписки, сообщения, счётчики непрочитанного, уведомления, сессии и
 * комната, лента, заявки), плюс свой профиль и присутствие, у которых событий нет. Такие запросы
 * на экране перечитываются всегда, даже если свежи по своему `staleTime`.
 *
 * Остальное живёт по своему `staleTime`. Раньше перечитывалось всё на экране, и возврат сети в
 * Discover стоил около шестидесяти запросов разом: статистика и часы в игре на каждой карточке,
 * которым пять минут свежести положены нарочно (замер на телефоне 16.09.2026).
 *
 * Состояния звонка здесь нет: оно живёт не в кеше запросов, а в своём сторе и приходит сокетом.
 */
const LIVE_QUERY_ROOTS: ReadonlySet<unknown> = new Set([
  ...INVALIDATION_TABLE.flatMap((row) => row.keys.map((key) => key.prefix[0])),
  'me',
  'presence',
  'playerAvailability',
]);

const isLive = (query: Query): boolean => LIVE_QUERY_ROOTS.has(query.queryKey[0]);

const needsCatchUp = (query: Query): boolean =>
  followsCatchUp(query) && (isLive(query) || query.isStale());

type FetchDirection = 'forward' | 'backward';

/**
 * Куда шла догрузка страницы, начатая до обрыва; `null` - догрузки нет. Её ответ приклеился бы к
 * первой странице из старых данных, и в списке появились бы дубли и дыры.
 */
const fetchingMoreDirection = (query: Query): FetchDirection | null =>
  query.state.fetchStatus === 'idle' ? null : (query.state.fetchMeta?.fetchMore?.direction ?? null);

async function refresh(queryClient: QueryClient): Promise<void> {
  const cache = queryClient.getQueryCache();
  const interrupted = cache.getAll().flatMap((query) => {
    const direction = fetchingMoreDirection(query);
    return direction ? [{ query, direction }] : [];
  });

  // Догрузку - отменить с откатом: перечитывание бесконечного списка идёт с первой страницы и
  // заменяет старое целиком.
  await Promise.all(interrupted.map(({ query }) => query.cancel({ revert: true })));
  // Живое, но не показанное сейчас, перечитается, когда экран откроют: запроса сейчас нет.
  await queryClient.invalidateQueries({ type: 'inactive', predicate: isLive, refetchType: 'none' });
  // Запрос, который уже идёт (например, продолжился сам после паузы), не перезапускается: иначе
  // по сети ушло бы два одинаковых.
  await queryClient.refetchQueries(
    { type: 'active', predicate: needsCatchUp },
    { cancelRefetch: false }
  );

  /*
    Отменённую догрузку - повторить, уже поверх свежей первой страницы. Сама она не повторится:
    список (Virtuoso) просит следующую страницу один раз на каждую длину, а длина после
    перечитывания та же, что до обрыва, - и список вставал, пока человек не уйдёт с экрана.
    Так же, как это делает `fetchNextPage`: тот же запрос с пометкой направления. Страницы
    дальше нет - запрос ничего не спросит у сервера.
  */
  for (const { query, direction } of interrupted) {
    if (cache.get(query.queryHash) === query && query.isActive()) {
      void query.fetch(undefined, { meta: { fetchMore: { direction } } }).catch(() => undefined);
    }
  }
}

/**
 * Перечитать экран, если за последние `CATCH_UP_DEDUPE_MS` этого ещё не делали. `delayMs` -
 * разброс для сокета: после перезапуска сервера сокетов приложения не должны прийти за данными
 * в одну секунду. `isCancelled` проверяется перед отложенным запуском. Возвращает `false`, если
 * вызов пропущен.
 */
export function catchUp(
  queryClient: QueryClient,
  reason: CatchUpReason,
  { delayMs = 0, isCancelled }: { delayMs?: number; isCancelled?: () => boolean } = {}
): boolean {
  const now = Date.now();
  const last = lastRunAt.get(queryClient);
  if (last !== undefined && now - last < CATCH_UP_DEDUPE_MS) {
    return false;
  }
  const stamp = now + delayMs;
  lastRunAt.set(queryClient, stamp);

  const start = () => {
    if (isCancelled?.()) {
      // Отменённый проход не должен отнимать окно у следующего.
      if (lastRunAt.get(queryClient) === stamp) {
        lastRunAt.delete(queryClient);
      }
      return;
    }
    Sentry.addBreadcrumb({ category: 'network', level: 'info', message: `catch-up: ${reason}` });
    void refresh(queryClient).catch(() => undefined);
  };

  if (delayMs > 0) {
    setTimeout(start, delayMs);
  } else {
    start();
  }
  return true;
}
